import { useCallback, useEffect, useRef, useState } from "react";

import { LogoutReason } from "@/constants/Login";
import { ScreenPath } from "@/constants/Navigation";
import { router } from "@/crossPlatform/expo-router";
import useAuth from "@/hooks/useAuth";
import useLoginInitialization from "@/hooks/useLoginInitialization";
import { useLogoutReasonSelector } from "@/store/reducers/auth";
import ErrorBoundaryUI from "@/ui/components/errorBoundary/ErrorBoundaryUI";
import EquipSplashScreen from "@/ui/components/splashScreen/EquipSplashScreen";
import { isWeb } from "@/utils/platform.util";

/**
 * Non-protected intermediate logout screen.
 *
 * This screen serves as a neutral route during logout that:
 * - Is accessible from any authentication state (no protected route guard)
 * - Unmounts all app components and cancels RTK Query subscriptions
 * - Preserves credentials for token revocation / cleanup API calls done during logout (native)
 * - Redirects to Auth0 universal logout (web)
 * - Shows splash screen during logout process
 *
 * Flow (All platforms):
 * 1. useAuth.logout() navigates here
 * 2. All app components unmount, RTK Query subscriptions cancel
 * 3. useLoginInitialization runs cleanup (auth'd API calls: deleteWaitingRoomAssessment, updateUserDeviceLoginState, revokeAuth0Token)
 *
 * Native: clearCredentials() is called, then navigate to Login
 * Web: clearWebSession redirects to Auth0 /v2/logout (clears session server-side), then Auth0 redirects back to app
 */
const LogoutScreen = () => {
  const logoutReason = useLogoutReasonSelector();
  const { clearWebSession } = useAuth();
  const [hasClearWebSessionFailed, setHasClearWebSessionFailed] =
    useState(false);

  // capture the initial logoutReason on mount via ref; it will be reset to JustPreLoginCleanup
  // by resetAllReduxSlices() during cleanup, but we need the original reason to decide
  // whether to run cleanup and navigate afterward
  const initialLogoutReasonRef = useRef(logoutReason);

  const isActualLogout =
    initialLogoutReasonRef.current !== LogoutReason.JustPreLoginCleanup;

  const onWebLogoutFinally = useCallback(
    async (reason: LogoutReason) => {
      if (!isWeb()) return;

      // this redirects to Auth0 /v2/logout, so soon after this executes, no further code is executed
      // hence why this must be in the finally block, as the last thing to run
      const response = await clearWebSession(reason);
      if (response?.error) {
        setHasClearWebSessionFailed(true);
      }
    },
    [clearWebSession],
  );

  const { isInitializationSuccess, isRestartRequired } = useLoginInitialization(
    {
      canLoginInitializationBegin: isActualLogout,
      onLogoutFinally: onWebLogoutFinally,
    },
  );

  // native-only: navigate to Login after cleanup completes
  // (web will have redirected away via clearWebSession before this runs)
  useEffect(() => {
    if (!isActualLogout || !isInitializationSuccess) return;
    router.replace(ScreenPath.Login);
  }, [isActualLogout, isInitializationSuccess]);

  if (isRestartRequired || hasClearWebSessionFailed) {
    return <ErrorBoundaryUI />;
  }
  return <EquipSplashScreen />;
};

export default LogoutScreen;
